import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { fetchArticle, fetchJournal } from "@/lib/api";
import Seo, { SITE_URL } from "@/components/seo/Seo";

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    fetchArticle(slug).then(setArticle).catch(() => {});
    fetchJournal().then((list) => setRelated(list.filter((a) => a.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (!article) {
    return (
      <div className="pt-40 px-6 md:px-12 lg:px-16 min-h-[60vh]">
        <p className="overline text-stone">Loading…</p>
      </div>
    );
  }

  return (
    <article data-testid="article-page" className="pt-28 md:pt-32">
      <Seo
        path={`/journal/${article.slug}`}
        title={article.title}
        description={article.excerpt || article.subtitle}
        image={article.cover_image}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt || article.subtitle,
          image: article.cover_image
            ? (article.cover_image.startsWith("http")
                ? article.cover_image
                : `${SITE_URL}${article.cover_image}`)
            : undefined,
          datePublished: article.published,
          dateModified: article.published,
          author: { "@type": "Person", name: article.author || "Still Form" },
          publisher: {
            "@type": "Organization",
            name: "Still Form",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.ico` },
          },
          mainEntityOfPage: `${SITE_URL}/journal/${article.slug}`,
          articleSection: article.category,
          keywords: (article.tags || []).join(", "),
        }}
      />
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-16 pb-12">
        <Link to="/journal" className="overline inline-flex items-center gap-2 text-stone hover:text-ink mb-10">
          <ArrowLeft size={12} /> Back to Journal
        </Link>
        <div className="max-w-4xl">
          <p className="overline mb-6">{article.category} · {article.read_time} · {article.published}</p>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-light leading-[1.0] tracking-[-0.02em] text-ink">
            {article.title}
          </h1>
          <p className="font-display italic text-xl md:text-2xl lg:text-3xl text-charcoal mt-8 leading-snug max-w-2xl">
            {article.subtitle}
          </p>
          <p className="overline mt-10 text-stone">By {article.author}</p>
        </div>
      </header>

      {/* Cover */}
      <div className="px-6 md:px-12 lg:px-16">
        <div className="img-zoom aspect-[16/9] md:aspect-[2/1] overflow-hidden">
          <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Body */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-3 lg:sticky lg:top-32 self-start hidden lg:block">
            <p className="overline mb-4">Tags</p>
            <ul className="space-y-1">
              {(article.tags || []).map((t) => (
                <li key={t} className="text-sm text-charcoal italic font-display">{t}</li>
              ))}
            </ul>
          </aside>

          <div className="lg:col-span-7 lg:col-start-4">
            {article.body.map((p, i) => (
              <p
                key={i}
                className={`text-lg md:text-xl text-ink font-light leading-[1.8] mb-8 ${i === 0 ? "drop-cap" : ""}`}
              >
                {p}
              </p>
            ))}

            <div className="hairline mt-16 pt-8">
              <p className="overline text-stone">End of dispatch</p>
              <p className="font-display italic text-2xl md:text-3xl text-charcoal mt-2 leading-tight">
                Read slowly. Return when needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="px-6 md:px-12 lg:px-16 py-16 border-t border-hairline">
        <p className="overline mb-10">Continue reading</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {related.map((r) => (
            <Link key={r.slug} to={`/journal/${r.slug}`} data-testid={`related-${r.slug}`} className="group">
              <div className="img-zoom aspect-[4/3] overflow-hidden mb-4 bg-clay">
                <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover" />
              </div>
              <p className="overline mb-1">{r.category}</p>
              <h3 className="font-display text-2xl text-ink leading-tight group-hover:italic transition-all">
                {r.title}
              </h3>
              <p className="text-sm text-charcoal mt-2 leading-relaxed font-light line-clamp-2">{r.excerpt}</p>
              <span className="inline-flex items-center gap-2 mt-3 text-[0.7rem] tracking-[0.22em] uppercase text-ink link-underline">
                Read <ArrowUpRight size={12} />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
}
